import './SessionExplorer.scss';
import { BaseWidget, WidgetAction } from 'models/WidgetModel';
import React, { useEffect, useState } from 'react';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import { SessionExplorerStyledDataGrid } from 'features/DataGrid/DataGridStyling';
import { PaginationState, SortingState, ColumnHeader } from '@cx/ui/components/DataGrid';
import LoadingSpinner from 'core-ui/LoadingSpinner/LoadingSpinner';
interface SessionExplorerProps extends BaseWidget {
  response: any;
  sessionClick: (row: any) => void;
  hideTranscript?: boolean;
  selectedSession?: any;
  onColumnsLoaded?: (columns: any[]) => void;
}

const SessionExplorer: React.FC<SessionExplorerProps> = ({
  response,
  sessionClick,
  hideTranscript,
  onColumnsLoaded,
}) => {
  const { context } = response;
  const [data, setData] = useState<any>([]);
  const [columns, setColumns] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const { endpoints, dashboardConfig, dateFilter } = useGlobalStore((state) => ({
    endpoints: state.endpoints,
    dashboardConfig: state.configDashboard,
    dateFilter: state.dateFilter,
  }));

  const { action, setState } = useWidgetContext();

  const { response: sessionExplorerResponse, axiosFetch, loading } = useAxios();
  useEffect(() => {
    if (!response) return;
    const getData = async () => {
      await getDataFromSingleEndpoint(
        endpoints.getWidgetData,
        {
          filters: {
            category_id: context.l2categoryguid,
            date_interval: dateFilter,
          },
          source: 'chat',
          actionName: 'sessions',

          ...(dashboardConfig.external
            ? {
                external: true,
              }
            : {
                external: false,
                mockEndpoint: 'session-explorer',
              }),
        },
        axiosFetch
      );
    };
    getData();
  }, [response]);

  useEffect(() => {
    if (action === WidgetAction.EXPANDABLE) {
      setState({ action: WidgetAction.NONE });
    }
  }, [action]);

  useEffect(() => {
    const tableData = sessionExplorerResponse?.data;
    if (tableData) {
      const displayedData = tableData?.data;
      setData(displayedData);
      setColumns(tableData?.columns);
      onColumnsLoaded?.(tableData?.columns);
      if (Array.isArray(displayedData) && displayedData.length > 0) {
        sessionClick(displayedData[0]);
      }
    }
  }, [sessionExplorerResponse]);

  const handleRowClick = (row: any) => {
    try {
      sessionClick(row);
    } catch (error: any) {
      console.error(`${error.message}, be sure you are running from CX application`);
    }
  };

  const getColumns = () => {
    if (!columns) return [];
    const columList = hideTranscript
      ? columns?.filter((item: any) => item?.ColName !== 'Summary')
      : columns;
    return columList?.map((col: any) => {
      return {
        id: col?.Field,
        accessorKey: col?.Field,
        header: ({ column }: { column: any }) => (
          <ColumnHeader column={column} title={col?.DisplayName} />
        ),
        cell: (cell: { [x: string]: string | any }) => {
          let rowValue = cell.row.original[col?.Field];

          if (typeof rowValue === 'string' && rowValue.length > 30) {
            rowValue = rowValue.substring(0, 30) + '...';
          }

          if (typeof rowValue === 'number' && rowValue % 1 != 0) {
            rowValue = rowValue.toFixed(2);
          }

          return (
            <div
              className={`pck-data-grid-ellipsis
              ${col?.Field === 'field_1' ? 'pck-value-width' : ''}`}
              onClick={() => handleRowClick(cell.row.original)}
            >
              {rowValue}
            </div>
          );
        },
      };
    });
  };

  if (loading) return <LoadingSpinner text="Loading sessions..." alias="DynamicTable" />;

  return (
    <div className={`session-explorer_dg-container`}>
      <div className={`session-explorer_data-grid session-explorer_sc-container`}>
        <SessionExplorerStyledDataGrid
          columns={getColumns() ?? []}
          data={data ?? []}
          pagination={paginationState}
          paginationOptions={{
            onPaginationChange: setPaginationState,
            manualPagination: false,
            rowCount: data.length,
            pageSizeOptions: [10, 5, 25, 50, 100],
          }}
          sorting={sorting}
          sortingOptions={{
            onSortingChange: setSorting,
            enableSortingRemoval: false,
          }}
          className="data-grid-table"
          isLoading={loading}
        />
      </div>
    </div>
  );
};

export default SessionExplorer;
